import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, SortableHeader, Table, cn } from '@wit-support/ui';
import { COLUMN_VIEW, groupTitle } from '../columns.js';
import { describeCampaignsError } from '../errors.js';
import { useCampaignChildren } from '../queries.js';
import { adSetToRow, adToRow, campaignToRow, sortRows, } from '../tree.js';
import { ASC_FIRST, DEFAULT_SORT_SHOWN, fmtMoney } from '../view.js';
import { MetaToggle, RowMenu } from './row-actions.js';
/*
 * Подсветка строки по дням паузы: ✕ 0–3 — красноватая, ✓ 4–5 — зеленоватая
 * (макет Main). Фон непрозрачный (смесь с фоном карточки): под закреплённой
 * ячейкой не должно просвечивать то, что прокручивается. Классы — литералы,
 * иначе сканер Tailwind их не найдёт.
 */
/** Янтарная полоса слева у названия: «включена, но внутри всё на паузе». */
const BREAK_BAR = 'shadow-[inset_3px_0_0_var(--color-state-warning-solid)]';
const TINT = {
    dangerStrong: 'bg-[color-mix(in_srgb,var(--color-state-danger-bg)_85%,var(--color-surface-raised))] data-[pin]:bg-[color-mix(in_srgb,var(--color-state-danger-bg)_85%,var(--color-surface-raised))]',
    dangerSoft: 'bg-[color-mix(in_srgb,var(--color-state-danger-bg)_40%,var(--color-surface-raised))] data-[pin]:bg-[color-mix(in_srgb,var(--color-state-danger-bg)_40%,var(--color-surface-raised))]',
    success: 'bg-[color-mix(in_srgb,var(--color-state-success-bg)_80%,var(--color-surface-raised))] data-[pin]:bg-[color-mix(in_srgb,var(--color-state-success-bg)_80%,var(--color-surface-raised))]',
};
function toneBg(row, grad) {
    if (!row.paused || row.pauseDays === null)
        return null;
    if (row.pauseDays <= 3)
        return grad && row.pauseDays >= 2 ? TINT.dangerSoft : TINT.dangerStrong;
    if (row.pauseDays <= 5)
        return TINT.success;
    return null;
}
function Cells({ row, ctx, columns, tint, }) {
    return (_jsx(_Fragment, { children: columns.map((col) => {
            const v = COLUMN_VIEW[col.id];
            return (_jsx(Table.Cell, { column: col, className: cn('whitespace-nowrap px-2 py-1.5', v.align === 'right' && 'text-right tabular-nums', col.id === 'name' && 'whitespace-normal', col.id === 'name' && row.allChildrenPaused && !row.paused && BREAK_BAR, tint), children: v.cell(row, ctx) }, col.id));
        }) }));
}
function ChildRows({ accountId, campaignId, parent, ctx, columns, sort, expandAllGroups, }) {
    const q = useCampaignChildren(accountId, campaignId, true, ctx.day === ctx.today ? undefined : ctx.day);
    const [open, setOpen] = useState(new Set());
    const sets = q.data?.adSets;
    // «Раскрыть все» раскрывает и группы; «Свернуть все» — сворачивает.
    useEffect(() => {
        setOpen(expandAllGroups && sets
            ? new Set(sets.filter((s) => s.ads.length > 0).map((s) => s.adSetId))
            : new Set());
    }, [expandAllGroups, sets]);
    const span = columns.length;
    const tree = useMemo(() => sortRows((sets ?? []).map((s) => ({
        row: adSetToRow(s, campaignId, false, ctx.currency, parent, ctx.caps),
        set: s,
    })), sort), [sets, sort, campaignId, ctx.currency, parent, ctx.caps]);
    if (q.isLoading) {
        return _jsx(Table.Loading, { colSpan: span, rows: 2, "data-testid": `children-loading-${campaignId}` });
    }
    if (q.isError) {
        return (_jsx(Table.Row, { "data-testid": `children-error-${campaignId}`, children: _jsxs(Table.Cell, { colSpan: span, className: "text-sm text-[var(--color-state-danger-fg)]", children: [describeCampaignsError(q.error), ' ', _jsx(Button, { type: "button", size: "sm", variant: "secondary", onClick: () => void q.refetch(), children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" })] }) }));
    }
    if (tree.length === 0) {
        return (_jsx(Table.Row, { children: _jsx(Table.Cell, { colSpan: span, className: "pl-16 text-sm text-[var(--color-text-muted)]", children: "\u0412\u043D\u0443\u0442\u0440\u0438 \u043D\u0435\u0442 \u0433\u0440\u0443\u043F\u043F." }) }));
    }
    return (_jsx(_Fragment, { children: tree.map(({ set }) => {
            const expanded = open.has(set.adSetId);
            const row = adSetToRow(set, campaignId, expanded, ctx.currency, parent, ctx.caps);
            const sctx = {
                ...ctx,
                onToggleExpand: () => setOpen((prev) => {
                    const next = new Set(prev);
                    if (next.has(set.adSetId))
                        next.delete(set.adSetId);
                    else
                        next.add(set.adSetId);
                    return next;
                }),
            };
            const ads = sortRows(set.ads.map((a) => ({ row: adToRow(a, campaignId, parent, ctx.caps), ad: a })), sort);
            return (_jsxs(Fragment, { children: [_jsx(Table.Row, { "data-testid": `row-${set.adSetId}`, children: _jsx(Cells, { row: row, ctx: sctx, columns: columns, tint: toneBg(row, ctx.grad) }) }), expanded
                        ? ads.map(({ row: ar, ad }) => (_jsx(Table.Row, { "data-testid": `row-${ad.adId}`, children: _jsx(Cells, { row: ar, ctx: ctx, columns: columns, tint: toneBg(ar, ctx.grad) }) }, `a-${ad.adId}`)))
                        : null] }, row.key));
        }) }));
}
/** Группы колонок шапки: соседние колонки одной группы — под общим заголовком. */
function headerGroups(columns) {
    const out = [];
    for (const col of columns) {
        const g = COLUMN_VIEW[col.id].group ?? null;
        const last = out[out.length - 1];
        if (g && last && last.group === g)
            last.span += 1;
        else
            out.push({ key: `${g ?? 'x'}-${col.id}`, span: 1, group: g, col });
    }
    return out;
}
/**
 * Таблица-дерево: кампания → группы → объявления. Дети грузятся при раскрытии
 * кампании (`/children`) и сортируются внутри родителя тем же столбцом, что
 * задан в шапке. Колонки — настройки пользователя (`useTableColumns`).
 */
export function CampaignsTable(props) {
    const { accountId, items, currency, handlers, columns, sort, onSortChange, selected, selectLimitReached, onToggleSelect, onToggleAll, day, today, anchorDay, paybackUnavailable, grad, caps, open, onToggleOpen, expandAllGroups, totalCampaigns, } = props;
    const maxSpend = Math.max(0, ...items.map((c) => c.spend.todayMinor ?? 0));
    const ctx = {
        currency,
        day,
        today,
        anchorDay,
        paybackUnavailable,
        selected,
        selectLimitReached,
        onToggleSelect,
        onToggleExpand: (r) => onToggleOpen(r.id),
        grad,
        caps,
        maxSpend,
        toggle: (r) => _jsx(MetaToggle, { row: r, h: handlers }),
        actions: (r) => _jsx(RowMenu, { row: r, h: handlers }),
        onOpenCard: handlers.onOpenCard,
    };
    // выбираются все: массовая пауза нужна и кампаниям запуска до старта (включение для них исключает превью)
    const selectable = items;
    const allOn = selectable.length > 0 && selectable.every((c) => selected.has(c.campaignId));
    const someOn = selectable.some((c) => selected.has(c.campaignId));
    const groups = headerGroups(columns);
    const hasGroups = groups.some((g) => g.group !== null);
    const shown = sort ?? DEFAULT_SORT_SHOWN;
    const sumSpend = items.reduce((s, c) => s + (c.spend.todayMinor ?? 0), 0);
    const sumPurch = items.reduce((s, c) => s + c.purchases.today, 0);
    const totalCell = (id) => {
        switch (id) {
            case 'name':
                return totalCampaigns > items.length
                    ? `Итого на странице · ${items.length} из ${totalCampaigns} кампаний`
                    : `Итого по ${items.length} ${items.length === 1 ? 'кампании' : 'кампаниям'}`;
            case 'spendToday':
                return fmtMoney(sumSpend, currency);
            case 'purchases':
                return sumPurch;
            case 'cppToday':
                return sumPurch > 0 ? fmtMoney(Math.round(sumSpend / sumPurch), currency) : '—';
            default:
                return null;
        }
    };
    return (_jsxs(Table, { density: "compact", className: "w-full table-fixed min-w-[1280px]", "data-testid": "campaigns-table", children: [_jsx("colgroup", { children: columns.map((col) => (_jsx("col", { ...(col.id === 'name' ? {} : { style: { width: col.def.minWidth } }) }, col.id))) }), _jsxs(Table.Header, { children: [hasGroups ? (_jsx(Table.Row, { className: "hover:bg-transparent", children: groups.map((g) => g.group === null ? (_jsx(Table.HeaderCell, { column: g.col, className: "h-6 pb-0" }, g.key)) : (_jsx(Table.HeaderCell, { colSpan: g.span, scope: "colgroup", className: "h-6 pb-0 text-center text-xs font-normal text-[var(--color-text-muted)]", children: _jsx("span", { className: "block border-b border-[var(--color-border-strong)] pb-0.5", children: groupTitle(g.group, { day, today, anchorDay }) }) }, g.key))) })) : null, _jsx(Table.Row, { children: columns.map((col) => {
                            const v = COLUMN_VIEW[col.id];
                            if (col.id === 'select')
                                return (_jsx(Table.HeaderCell, { column: col, className: "px-2", children: _jsx(Checkbox, { "aria-label": "\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u0432\u0441\u0435 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438 \u043D\u0430 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0435", checked: allOn ? true : someOn ? 'indeterminate' : false, disabled: selectable.length === 0, onCheckedChange: (x) => onToggleAll(x === true) }) }, col.id));
                            return (_jsx(SortableHeader, { column: col, sortKey: v.sortKey, sort: shown, onSortChange: onSortChange, align: v.align ?? 'left', defaultDirection: ASC_FIRST.has(v.sortKey) ? 'asc' : 'desc', indicator: "end", className: cn('px-2 leading-tight', col.id !== 'budget' && col.id !== 'payback' && 'whitespace-nowrap'), ...(v.hint ? { hint: v.hint } : {}), children: v.fullName ? (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": true, children: v.header }), _jsx("span", { className: "sr-only", children: v.fullName })] })) : (v.header) }, col.id));
                        }) })] }), _jsxs(Table.Body, { children: [items.map((c) => {
                        const expanded = open.has(c.campaignId);
                        const row = campaignToRow(c, expanded, currency, caps);
                        const tint = toneBg(row, grad);
                        return (_jsxs(Fragment, { children: [_jsx(Table.Row, { "data-testid": `row-${c.campaignId}`, "data-state": selected.has(c.campaignId) ? 'selected' : undefined, children: _jsx(Cells, { row: row, ctx: ctx, columns: columns, tint: tint }) }), expanded ? (_jsx(ChildRows, { accountId: accountId, campaignId: c.campaignId, parent: c, ctx: { ...ctx, selected }, columns: columns, sort: sort, expandAllGroups: expandAllGroups })) : null] }, row.key));
                    }), _jsx(Table.Row, { "data-testid": "total-row", className: "font-semibold hover:bg-transparent", children: columns.map((col) => (_jsx(Table.Cell, { column: col, className: cn('whitespace-nowrap border-t border-[var(--color-border-strong)] px-2', COLUMN_VIEW[col.id].align === 'right' && 'text-right tabular-nums'), children: totalCell(col.id) }, col.id))) })] })] }));
}
//# sourceMappingURL=campaigns-table.js.map